import { ToolCategory, ToolType } from "~/domains/tools/types/ToolType";
import {
  CARRIAGES_CONFIGURATOR_URL,
  GEARBOX_SELECTION_ASSISTANT_URL,
  HEATER_SELECTION_ASSISTANT_URL,
  HOUSING_SELECTION_ASSISTANT_URL,
  INCH_SHAFT_CONFIGURATOR_URL,
  LINEAR_CONFIGURATOR_URL,
  LUBRICATION_SELECTION_ASSISTANT_URL,
  METRIC_SHAFT_CONFIGURATOR_URL,
  RAILS_CONFIGURATOR_URL,
  SELECTION_ASSISTANT,
} from "~/utils/url/staticUrls";
import { resolveCdnImageURL } from "~/utils/url/urlResolver";
import { toMapFn } from "~/common/utils/reducers";

interface CommonToolConfig {
  type: ToolType;
  url: string;
  imageUrl: string;
  pdpLaunchLabel: string;
  title: string;
  name: string;
}

export type LaunchToolConfig = LaunchAssistantConfig | LaunchConfiguratorConfig;

export interface LaunchConfiguratorConfig extends CommonToolConfig {
  category: ToolCategory.Configurator;
  launchLabel: string;
}

export interface LaunchAssistantConfig extends CommonToolConfig {
  category: ToolCategory.Assistant;
  launchLabel: string;
}

export function isAssistantConfig(config: LaunchToolConfig): config is LaunchAssistantConfig {
  return config.category === ToolCategory.Assistant;
}
export function isConfiguratorConfig(config: LaunchToolConfig): config is LaunchConfiguratorConfig {
  return config.category === ToolCategory.Configurator;
}

const CONFIG_LIST: LaunchToolConfig[] = [
  {
    type: ToolType.System,
    category: ToolCategory.Configurator,
    url: LINEAR_CONFIGURATOR_URL,
    imageUrl: resolveCdnImageURL("configure_system.png"),
    launchLabel: "feature.launchConfigurator.system.launch",
    pdpLaunchLabel: "feature.launchConfigurator.generic.launch",
    title: "feature.launchConfigurator.system.title",
    name: "cmsnavigationnode.schaefflerContentCatalog.FlyoutLinearConfiguratorNavNodetranslation",
  },
  {
    type: ToolType.Rail,
    category: ToolCategory.Configurator,
    url: RAILS_CONFIGURATOR_URL,
    imageUrl: resolveCdnImageURL("configure_rail.png"),
    launchLabel: "feature.launchConfigurator.rail.launch",
    pdpLaunchLabel: "feature.launchConfigurator.generic.launch",
    title: "feature.launchConfigurator.rail.title",
    name: "cmsnavigationnode.schaefflerContentCatalog.cmsitem.00117271translation",
  },
  {
    type: ToolType.Carriage,
    category: ToolCategory.Configurator,
    url: CARRIAGES_CONFIGURATOR_URL,
    imageUrl: resolveCdnImageURL("configure_carriage.png"),
    launchLabel: "feature.launchConfigurator.carriage.launch",
    pdpLaunchLabel: "feature.launchConfigurator.generic.launch",
    title: "feature.launchConfigurator.carriage.title",
    name: "page.configurator.carriage",
  },
  {
    type: ToolType.Rotary,
    category: ToolCategory.Assistant,
    url: SELECTION_ASSISTANT,
    imageUrl: "https://cdn.schaeffler-ecommerce.com/frontend/Rillenkugellager_00014D0C_DGBB.png",
    launchLabel: "feature.launchConfigurator.rotaryAssistant.launch",
    pdpLaunchLabel: "feature.launchConfigurator.generic.launchAssistant",
    title: "feature.launchConfigurator.rotaryAssistant.title",
    name: "cmsnavigationnode.schaefflerContentCatalog.BearingSelectionAssistantKSNavNodetranslation",
  },
  {
    type: ToolType.Housing,
    category: ToolCategory.Assistant,
    url: HOUSING_SELECTION_ASSISTANT_URL,
    imageUrl: "https://cdn.schaeffler-ecommerce.com/cdn/0001ACB0_d.png",
    launchLabel: "feature.launchConfigurator.housingAssistant.launch",
    pdpLaunchLabel: "feature.launchConfigurator.generic.launchAssistant",
    title: "feature.launchConfigurator.housingAssistant.title",
    name: "cmsnavigationnode.schaefflerContentCatalog.SelectionAssistantHousingNavNodetranslation",
  },
  {
    type: ToolType.Heater,
    category: ToolCategory.Assistant,
    url: HEATER_SELECTION_ASSISTANT_URL,
    imageUrl: "https://cdn.schaeffler-ecommerce.com/frontend/Heater.jpg",
    launchLabel: "feature.launchConfigurator.heaterAssistant.launch",
    pdpLaunchLabel: "feature.launchConfigurator.generic.launchAssistant",
    title: "feature.launchConfigurator.heaterAssistant.title",
    name: "cmsnavigationnode.schaefflerContentCatalog.HeatingManagerNavNodetranslation",
  },
  {
    type: ToolType.Gearbox,
    category: ToolCategory.Assistant,
    url: GEARBOX_SELECTION_ASSISTANT_URL,
    imageUrl: "",
    launchLabel: "feature.launchConfigurator.gearboxAssistant.launch",
    pdpLaunchLabel: "feature.launchConfigurator.generic.launchAssistant",
    title: "feature.launchConfigurator.gearboxAssistant.title",
    name: "page.sa.gearbox.title",
  },
  {
    type: ToolType.Lubrication,
    category: ToolCategory.Assistant,
    url: LUBRICATION_SELECTION_ASSISTANT_URL,
    imageUrl: "",
    launchLabel: "feature.launchConfigurator.lubricationAssistant.launch",
    pdpLaunchLabel: "feature.launchConfigurator.generic.launchAssistant",
    title: "feature.launchConfigurator.lubricationAssistant.title",
    name: "breadcrumb.lubrication.selection.assistant",
  },
  {
    type: ToolType.MetricShaft,
    category: ToolCategory.Configurator,
    url: METRIC_SHAFT_CONFIGURATOR_URL,
    imageUrl: "",
    launchLabel: "feature.launchConfigurator.metricShaft.launch",
    pdpLaunchLabel: "feature.launchConfigurator.generic.launchAssistant",
    title: "feature.launchConfigurator.metricShaft.title",
    name: "configurator.shaft.title.metric",
  },
  {
    type: ToolType.InchShaft,
    category: ToolCategory.Configurator,
    url: INCH_SHAFT_CONFIGURATOR_URL,
    imageUrl: "",
    launchLabel: "feature.launchConfigurator.inchShaft.launch",
    pdpLaunchLabel: "feature.launchConfigurator.generic.launchAssistant",
    title: "feature.launchConfigurator.inchShaft.title",
    name: "configurator.shaft.title.inch",
  },
];

const TOOL_CONFIG: Dict<LaunchToolConfig> = toMapFn(CONFIG_LIST, (item) => item.type);

export function getToolConfiguration(type: ToolType): LaunchToolConfig {
  if (import.meta.env.DEV) {
    if (!TOOL_CONFIG[type]) {
      throw new Error("Unsupported value of ToolType");
    }
  }
  return TOOL_CONFIG[type];
}
